import { TutorReportsHelperService } from '../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, TemplateRef, Optional } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AuthenticationService, PlacementService, SessionService, UserService } from '../../_services/index';
import { ActivatedRoute, Router, NavigationExtras } from "@angular/router";
import { CrmUserComponent } from '../../shared/user/crm.user.component';
import { ClientDataHelper } from '../../_helpers/client-data-helper';

import { environment } from '../../../environments/environment';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { ColumnDef, DEFAULT_PAGE_SIZE, PageChange, SortChange, SortDirection, StatusVariant } from '../../shared/data-table/data-table.model';
import { readRememberedPageSize } from '../../shared/data-table/page-size-storage';
import { SessionGroup } from '../../_models/session';
import { PagedRequest, PagedResponse } from '../../_models/common/paged-response.model';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { ATTENDANCE_OUTCOME_OPTIONS, ORDER_TYPE_OPTIONS, REPORT_STATUS_OPTIONS } from '../../_constants/search-filter-options';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, resolveDatePreset, toIsoDate } from '../../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../../shared/filter-ux/date-presets.component';
import { ClientSuggestionsService } from '../../shared/typeahead/client-suggestions.service';
import { SuggestService } from '../../_services/suggest/suggest.service';

/**
 * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
 * only: free text (learner, tutor, client, referrer) and the staff picker stay out of the URL (browser
 * history, telemetry) and browser storage.
 */
const FILTER_SPEC: FilterSpec = {
  brand: 'number[]', delivery: 'string', orderType: 'string', report: 'string[]', attendance: 'string[]',
  from: 'string', to: 'string', preset: 'string'
};

/** The filters.session-search parameters: sent, and restored from a saved search, only while it is on. */
const GATED_PARAMS = ['companyIds', 'aPorSP', 'reportStatuses', 'attendanceOutcomes'];

@Component({
    selector: 'session-search',
    templateUrl: 'sessions.search.component.html',
    styleUrls: ['sessions.search.component.css'],
    standalone: false
})
export class SessionsSearchComponent implements OnInit, AfterViewInit, OnDestroy {

  rows: any[] = [];
  columns: ColumnDef[] = [];

  @ViewChild(CrmUserComponent, { static: false }) crmUser;

  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  showResults: Boolean = false;
  searchResultText: string = "";
  errorMsg: string = "";
  showSearchExtraColumns: Boolean = false;
  recordCount: number = null;
  loading: boolean = false;
  tutorIds: any = []
  users: any = [];
  searchData: string = 'sessionsSearch';

  /**
   * Server-side paging state (Plans/table-lazy-loading.md §4 A2). This screen always opts in to
   * paging - `totalCount` drives the pager's "of Z" footer and must never be derived from
   * `rows.length`, which is only ever one page's worth.
   */
  public totalCount: number = 0;
  public pageNumber: number = 1;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public sortField: string = null;
  public sortDir: SortDirection = 'asc';
  /** Key under which the table remembers the user's page-size choice for the session. */
  public readonly tableId: string = 'sessions-search';

  /** The filter criteria of the most recently issued search - reused by page/sort re-fetches so
   * both call sites into the service (the fresh search and the restored-search flow below) share
   * one page/sort state and cannot disagree about which page is showing. */
  private lastSearchParams: any = null;

  /** Search requests are funnelled through this subject and switchMap'd so a rapid page/sort click
   * cannot have an earlier, slower response land after a later one (last-write-wins). */
  private readonly searchRequested = new Subject<void>();
  private readonly destroyed = new Subject<void>();

  private staffFilter: boolean = false;
  private readOnly: boolean = false;

  public pupilPlaceholderTooltip: string;

  /** For the template's `*appFeatureSwitch`. */
  readonly featureKeys = FeatureSwitchKeys;
  readonly orderTypeOptions = ORDER_TYPE_OPTIONS;
  readonly reportStatusOptions = REPORT_STATUS_OPTIONS;
  readonly attendanceOutcomeOptions = ATTENDANCE_OUTCOME_OPTIONS;
  /**
   * The filters.session-search switch. Off (the default, and on any failure): the Brand, Order type,
   * Report status and Attendance filters don't show and the search sends none of their parameters.
   */
  public filtersEnabled: boolean = false;
  /** Selected brands, sent as SessionParamsRequest.CompanyIds; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** SessionParamsRequest.APorSP: "AP" / "SP"; null means both. */
  public aPorSP: string = null;
  public reportStatuses: string[] = [];
  public attendanceOutcomes: string[] = [];
  /** The date preset behind Date From / Date To, or null when they were set by hand. */
  public datePreset: DatePreset | null = null;
  /** Remembered filters and filter state in the URL; used only while filters.session-search is on. */
  private filterUx: ScreenFilterUx;
  /** search.autocomplete.session-search: the Client, Tutor and Client Referrer boxes suggest matches. Off, they are plain boxes. */
  public autocompleteEnabled: boolean = false;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private sessionService: SessionService,
    private userService: UserService,
    public router: Router,
    private featureHelperService: FeatureHelperService,
    private placementService: PlacementService,
    private clientDataHelper: ClientDataHelper,
    private tutorReportsHelperService: TutorReportsHelperService,
    private authService: AuthenticationService,
    public themeService: ThemeService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null,
    @Optional() public clientSuggestions: ClientSuggestionsService = null,
    @Optional() public suggest: SuggestService = null
  ) { }
  frmSessionSearch: UntypedFormGroup;

  ngOnInit() {
    // The first fetch is issued before the table renders, so the remembered size must be read here
    // rather than left to the table - otherwise the request and the footer disagree on page size.
    this.pageSize = readRememberedPageSize(this.tableId) ?? DEFAULT_PAGE_SIZE;

    let featureList = new Array<string>();
    featureList.push("SessionSearchView", "StaffFilter");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "SessionSearchView")[0].enabled) {
      console.error("User does not have the required permissions to view the sessions search screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
      this.authenticationStateHelper.unauthorizedToken = true;
    }
    this.staffFilter = currentUserFeatures.filter(f => f.feature === "StaffFilter")[0].enabled;

    // Bug 37026: tutor scoping belongs to the server, not the browser. GetSessionsV2 now
    // derives it from the caller's ExternalApplicationUserId. Reading ids out of localStorage
    // made the browser the authority, and the server read an empty list as "every tutor", so
    // the least-privileged request returned the most data. Only the column choice below still
    // depends on the login type.
    if (!this.staffFilter) {
      this.showSearchExtraColumns = true;
    }

    this.readOnly = this.featureHelperService.getUserFeatures()
      .some(f => f.feature === 'TutorReportsView' && f.enabled) === false;

    this.searchRequested.pipe(
      switchMap(() => {
        this.loading = true;
        return this.sessionService.searchSessions(this.lastSearchParams, this.buildPaging());
      }),
      takeUntil(this.destroyed)
    ).subscribe({
      next: (response: PagedResponse<any>) => {
        this.loading = false;
        this.clientDataHelper.storeData(this.lastSearchParams, this.searchData);
        this.populateGrid(response);
      },
      error: error => {
        this.loading = false;
        if (error.status === 401) {
          this.router.navigate(['/login']);
        } else {
          this.searchResultText = "No Results Found";
          this.recordCount = 0;
          this.totalCount = 0;
        }
      }
    });

    this.buildColumns();

    // Defaults: from = one month ago, to = today (native date inputs take yyyy-MM-dd strings).
    this.frmSessionSearch = this.formBuilder.group({
      startDate: [new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10)],
      endDate: [new Date().toISOString().slice(0, 10)],
      student: null,
      tutor: null,
      clientReferrer: null,
      client: null,
      submit: null,
      user: null,
      // null = "All". Sent straight through to the API as SessionParamsRequest.DeliveryMode, which
      // filters in SQL on OrbitLeapAPI's side rather than trimming an already-paged page client-side —
      // paging the filtered set is the whole point, otherwise page 2 of "Online" would be wrong.
      deliveryMode: null
    });
    var currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.placementService.GetCrmUserAccounts().subscribe(data => {
      this.users = data;
      var currUser = this.users.find(x => x.email === currentUser.username);
      this.frmSessionSearch.get("user").setValue(currUser);
    });
    // A saved search is restored only once the switch is known, so it cannot bring back filters
    // the screen does not show.
    this.filterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersSessionSearch, FILTER_SPEC, this.router, this.route);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteSessionSearch).pipe(takeUntil(this.destroyed))
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersSessionSearch).pipe(takeUntil(this.destroyed)).subscribe(enabled => {
      this.filtersEnabled = enabled;
      // With the switch on, a shared link's filters win. Otherwise this tab's last search is restored as
      // before, and failing that, this user's remembered filters apply.
      const fromUrl = enabled ? this.filterUx.urlState() : null;
      if (fromUrl) {
        this.applyFilterState(fromUrl);
        this.searchSession();
        return;
      }
      const hasTabSearch = this.clientDataHelper.getData(this.searchData) != null;
      this.setClientData();
      const remembered = enabled && !hasTabSearch ? this.filterUx.rememberedState() : null;
      if (remembered) {
        this.applyFilterState(remembered);
        this.searchSession();
      }
    });

  }

  private buildColumns(): void {
    const studentHeader = 'Learner';
    const columns: ColumnDef[] = [
      { field: 'startDate', header: 'Date', type: 'date', width: '12%' },
      { field: 'time', header: 'Time', type: 'template', templateName: 'time' },
      // Sits next to the time so the "is this online?" question is answered in the same glance as
      // "when is it?", rather than at the far right of the row. width 1% collapses the column to its
      // natural minimum — the <col> width is a suggestion the browser shrinks to fit, and the pill is
      // white-space: nowrap, so the widest value ("In person") still fits on one line.
      { field: 'deliveryMode', header: 'Delivery', type: 'pill', width: '1%', pillMap: row => this.deliveryPill(row) }
    ];
    if (this.showSearchExtraColumns) {
      columns.push({ field: 'tutorFullName', header: 'Tutor' });
    }
    columns.push({ field: 'student', header: studentHeader, type: 'template', templateName: 'student' });
    columns.push({ field: 'subject', header: 'Session' });
    if (this.showSearchExtraColumns) {
      columns.push({ field: 'clientReferrer', header: 'Client Referrer' });
    }
    columns.push({ field: 'client', header: 'Client' });
    this.columns = columns;
  }

  /** Online bookings expect a Digital Classroom, so they get the stand-out variant. */
  private deliveryPill(row: SessionGroup): StatusVariant {
    return (row.deliveryMode || '').toLowerCase() === 'online' ? 'blue' : 'gray';
  }

  ngAfterViewInit() {
    let searchData = this.clientDataHelper.getData(this.searchData);
    if (this.crmUser != null && searchData != null) {
      this.crmUser.UserData.subscribe({
        next: (n) =>
          this.crmUser.setUsers(searchData.user)
      });
    }

    this.pupilPlaceholderTooltip = "Please enter the learner";
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  /** Builds the paging/sort fields sent alongside whichever filter params triggered the search. */
  private buildPaging(): PagedRequest {
    return {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortDirection: this.sortDir
    };
  }

  /** Records the filter criteria for the search pipeline to use and issues it via the switchMap'd
   * subject - shared by both call sites (a fresh search and the restored-search flow below) so
   * page/sort state can never disagree between them. */
  private runSearch(params: any): void {
    this.lastSearchParams = params;
    this.searchRequested.next();
  }

  setClientData() {
    let searchData = this.clientDataHelper.getData(this.searchData);
    if (searchData != null) {
      this.frmSessionSearch.get("tutor").setValue(searchData.tutor);
      this.frmSessionSearch.get("startDate").setValue(searchData.startDate);
      this.frmSessionSearch.get("endDate").setValue(searchData.endDate);
      this.frmSessionSearch.get("student").setValue(searchData.student);
      this.frmSessionSearch.get("clientReferrer").setValue(searchData.clientReferrer);
      this.frmSessionSearch.get("submit").setValue(searchData.submit);
      this.frmSessionSearch.get("user").setValue(searchData.user);
      this.frmSessionSearch.get("client").setValue(searchData.client);
      // Restored searches must carry the delivery filter too, or returning to this screen silently
      // widens the result set while the dropdown still reads as filtered.
      this.frmSessionSearch.get("deliveryMode").setValue(searchData.deliveryMode ?? null);
      if (this.filtersEnabled) {
        this.companyIds = searchData.companyIds ?? [];
        this.aPorSP = searchData.aPorSP ?? null;
        this.reportStatuses = searchData.reportStatuses ?? [];
        this.attendanceOutcomes = searchData.attendanceOutcomes ?? [];
      } else {
        // Saved while the switch was on: re-run it in the pre-filter shape.
        GATED_PARAMS.forEach(key => delete searchData[key]);
      }

      setTimeout(() => {
        this.pageNumber = 1;
        this.runSearch(searchData);
      }, 1)
    }
  }

  searchSession() {
    this.resetValues();
    if (!this.frmSessionSearch.valid) {
      return;
    }
    var stDate = this.frmSessionSearch.value.startDate;
    var endDate = this.frmSessionSearch.value.endDate;
    if (stDate !== null && endDate !== null) {
      if (stDate > endDate) {
        this.errorMsg = "Date From cannot be later than Date To";
        return;
      }
    }
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
    var params = this.frmSessionSearch.value;
    params.startDate = new Date(this.setDate(this.frmSessionSearch.value.startDate));
    params.endDate = new Date(this.setDate(this.frmSessionSearch.value.endDate));
    params.includeFutureSessions = true;
    params.tutorIds = this.tutorIds;
    params.isSearch = true;
    // New parameters only when the switch is on; off, the request is the pre-filter shape.
    if (this.filtersEnabled) {
      params.companyIds = this.companyIds ?? [];
      params.aPorSP = this.aPorSP || null;
      params.reportStatuses = this.reportStatuses ?? [];
      params.attendanceOutcomes = this.attendanceOutcomes ?? [];
    }
    if (this.crmUser) {
      params.user = this.crmUser.selectedAllUsers ? null : this.crmUser.frmCrmUser.value.user;
    }
    else {
      params.user = null;
    }
    // A new search always starts back at page 1 - staying on, say, page 7 of the previous
    // search's results would silently show the wrong rows for the new one.
    this.pageNumber = 1;
    this.runSearch(params);
  }

  onDatePreset(selection: DatePresetSelection) {
    this.datePreset = selection.preset;
    this.frmSessionSearch.patchValue({ startDate: selection.from, endDate: selection.to });
  }

  /** Clear all: back to the last month, forgotten, and out of the URL. The results are left as they are. */
  clearAll() {
    this.frmSessionSearch.patchValue({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10),
      endDate: new Date().toISOString().slice(0, 10),
      student: null, tutor: null, clientReferrer: null, client: null, submit: null, deliveryMode: null
    });
    this.companyIds = [];
    this.aPorSP = null;
    this.reportStatuses = [];
    this.attendanceOutcomes = [];
    this.datePreset = null;
    this.clientDataHelper.storeData(null, this.searchData);
    this.filterUx.clear();
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    // Read the controls: searchSession() overwrites frmSessionSearch.value's dates with Date objects.
    const value = this.frmSessionSearch.getRawValue();
    return {
      brand: this.companyIds,
      delivery: value.deliveryMode,
      orderType: this.aPorSP,
      report: this.reportStatuses,
      attendance: this.attendanceOutcomes,
      preset: this.datePreset,
      from: this.datePreset ? null : SessionsSearchComponent.isoDate(value.startDate),
      to: this.datePreset ? null : SessionsSearchComponent.isoDate(value.endDate)
    };
  }

  private applyFilterState(state: FilterState) {
    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    this.frmSessionSearch.patchValue({
      startDate: range ? range.from : (state.from ?? this.frmSessionSearch.get('startDate').value),
      endDate: range ? range.to : (state.to ?? this.frmSessionSearch.get('endDate').value),
      deliveryMode: state.delivery ?? null
    });
    this.companyIds = (state.brand as number[]) ?? [];
    this.aPorSP = (state.orderType as string) ?? null;
    this.reportStatuses = (state.report as string[]) ?? [];
    this.attendanceOutcomes = (state.attendance as string[]) ?? [];
  }

  /** The form holds a yyyy-MM-dd string until the user edits the date, then a Date. */
  private static isoDate(value: any): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : toIsoDate(date);
  }

  /** A page click or page-size change from the table - re-issues the last search for the new page. */
  public onPageChange(event: PageChange): void {
    this.pageNumber = event.page;
    this.pageSize = event.pageSize;
    this.searchRequested.next();
  }

  /**
   * A column header sort. Only records the new sort state here - the table also emits `pageChange`
   * (reset to page 1) for server-side sorting, and that handler is what re-issues the request, so a
   * sort click results in exactly one request carrying both the sort and the page reset.
   */
  public onSortChange(event: SortChange): void {
    this.sortField = event.field;
    this.sortDir = event.direction;
  }

  populateGrid(response: PagedResponse<any>) {
    const items = response && response.items ? response.items : [];
    this.totalCount = response ? response.totalCount : 0;
    this.recordCount = items.length;
    if (items.length > 0) {
      this.rows = items;
      this.showResults = true;
    }
    else {
      this.searchResultText = "No Results Found";
    }
  }

  validateForm(): boolean {
    var ret = false;
    Object.keys(this.frmSessionSearch.controls).forEach(key => {
      if (this.frmSessionSearch.controls[key].value !== null) {
        ret = true;
      }
    });
    return ret;
  }

  resetValues() {
    this.searchResultText = '';
    this.recordCount = 0;
    this.errorMsg = '';
  }

  onDataclicked(row) {
    if (this.readOnly) {
      return;
    }
    this.tutorReportsHelperService.setCommonDataBeforeNavigate(row.placementID, row.startDate, row.endDate);
    setTimeout(() => {
      this.router.navigate(["/tutorreports", row.placementID]);
    }, 200);
  }

  setDate(date) {
    var d = new Date(date);
    return d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  }
}
